import { useMemo } from "react"
import useResource from "hooks/resources"
import { OK, ERROR, LOADING, combineStatuses, DataStatus } from "helpers/data-statuses"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import {
  CoverageResourceType,
  MODELS_AVAILABILITY_QUERY,
  ModelAvailability,
  CoverageModelDate,
  getCoverageModelsDates,
  getWindowedCoverageModelsDates,
  normalizeCoverageMap,
  mergeModelsWithCoverage,
  buildModelsCoverageRequest,
} from "helpers/models"
import { useAccountAlias } from "resources/account"
import { useModelMetadata, Model } from "./models"
import { usePositionSetMetadata } from "./position-sets"
import { usePortfolioUploadDates } from "./portfolios"

const EMPTY_MODELS_DATES: CoverageModelDate[] = []
const EMPTY_NO_RESTRICTION_MODELS: string[] = []
const WINDOWED_COVERAGE_RESOURCE_TYPES: CoverageResourceType[] = ["portfolio", "swap"]

function useModelsAvailability(enabled: boolean) {
  return useResource<ModelAvailability[]>({
    resource: "modelsAvailability",
    url: () => (enabled ? { id: "models", query: MODELS_AVAILABILITY_QUERY } : null),
  }, "models")
}

// Position sets (portfolio/swap) need the asset's own date range to match Portfolio Context's
// windowed coverage check. Portfolio sources it from its own upload-date range (model-independent
// — see usePortfolioUploadDates), matching modules/model-selector/selectors' getEarliestUploadedDate
// /getLastestUploadedDate. This must not depend on a model being selected: the coverage gate needs
// to apply to the very first default-model pick, not just to switches after one already exists.
// Swap instead reuses the same usePositionSetMetadata/useModelMetadata queries dashboard.jsx already
// calls for instrumentMetadata (React Query dedupes by key, so this is a cache read, not a second
// network round trip) — it never requires bounds (requireBounds: false below), so the modelId
// dependency there doesn't gate the coverage check the way it would for portfolio.
function useAssetDateBounds(needsWindow: boolean, requireBounds: boolean, resourceType: CoverageResourceType | null, resourceId: string | null, instrument: SelectedInstrument | null, modelId: string | null) {
  const isPortfolio = needsWindow && resourceType === "portfolio"

  const [portfolioUploadDates, portfolioUploadDatesStatus] = usePortfolioUploadDates(isPortfolio ? resourceId : null)

  const [modelMetadata, modelMetadataStatus] = useModelMetadata(needsWindow && !isPortfolio ? modelId ?? "" : "")
  const currentDate = needsWindow && !isPortfolio ? modelMetadata?.availability?.currentDate : undefined
  const [positionSetMetadata, positionSetStatus] = usePositionSetMetadata(
    needsWindow && !isPortfolio ? modelId : null,
    needsWindow && !isPortfolio && instrument ? instrument : undefined,
    currentDate,
  )

  return useMemo(() => {
    if (!needsWindow) {
      return { earliestDate: null, latestDate: null, status: OK }
    }
    const earliestDate = isPortfolio ? portfolioUploadDates?.availableFrom ?? null : positionSetMetadata?.earliestDate ?? null
    const latestDate = isPortfolio ? portfolioUploadDates?.latestUploadedDate ?? null : positionSetMetadata?.latestDate ?? null
    const fetchStatus = isPortfolio ? portfolioUploadDatesStatus : combineStatuses([modelMetadataStatus, positionSetStatus])
    // A successful fetch with no usable date bounds (empty or stale-selected position set) must
    // route through the fail-open branch below, not the success branch with an empty coverage map
    // — otherwise every model but the selected one reads as uncovered instead of failing open.
    // Only applies when this resource actually requires bounds (portfolio) — swap computes its
    // window without them, matching modules/model-selector/selectors' requireBounds: false.
    const status = requireBounds && fetchStatus === OK && (!earliestDate || !latestDate) ? ERROR : fetchStatus
    return { earliestDate, latestDate, status }
  }, [needsWindow, requireBounds, isPortfolio, portfolioUploadDates, portfolioUploadDatesStatus, positionSetMetadata, modelMetadataStatus, positionSetStatus])
}

function useCoverageMap(resourceType: CoverageResourceType | null, resourceId: string | null, modelsDates: CoverageModelDate[]) {
  const account = useAccountAlias()

  const definition = useMemo(() => ({
    resource: "modelsCoverage" as const,
    url: () => buildModelsCoverageRequest({ modelsDates, resourceType: resourceType ?? "", resourceId: resourceId ?? "", account }),
  }), [modelsDates, resourceType, resourceId, account])
  const [rawCoverageData, status] = useResource<unknown>(definition, [])

  return useMemo(() => {
    if (status !== OK || !resourceType) {
      return [null, status] as const
    }
    return [normalizeCoverageMap(rawCoverageData, resourceType), OK] as const
  }, [rawCoverageData, status, resourceType])
}

export interface ModelWithCoverage extends Model {
  hasCoverage: boolean
}

export interface ModelsWithCoverageResult {
  models: (Model | ModelWithCoverage)[] | null
  status: DataStatus
  requiresResourceCoverage: boolean
}

/**
 * Wraps a plain model list with the coverage gate for a selected resource (portfolio/swap/etf/index),
 * mirroring the gate modules/model-selector already applies for Portfolio Context and Composite Asset.
 * If the coverage check itself fails to load, this fails open (requiresResourceCoverage: false) rather
 * than blocking model selection on an unrelated fetch error.
 */
export function useModelsWithCoverage(
  models: Model[] | null,
  modelsStatus: DataStatus,
  resourceType: CoverageResourceType | null,
  resourceId: string | null,
  instrument: SelectedInstrument | null,
  selectedModelId: string | null,
  // Only meaningful for "portfolio" — whether the selected portfolio rolls its positions forward
  // to the current date. Swap has no live/historical distinction (always treated as live); null
  // (portfolio not yet resolved) falls back to live, matching this hook's prior behavior.
  portfolioLive: boolean | null = null,
): ModelsWithCoverageResult {
  const wantsCoverage = !!(resourceType && resourceId)
  const needsWindow = wantsCoverage && resourceType !== null && WINDOWED_COVERAGE_RESOURCE_TYPES.includes(resourceType)
  // Swap never required asset availability bounds (unlike portfolio) — see
  // modules/model-selector/selectors' requireBounds: false for the same distinction.
  const requiresDateBounds = needsWindow && resourceType !== "swap"

  const [availabilityModels, availabilityStatus] = useModelsAvailability(wantsCoverage)
  const { earliestDate, latestDate, status: dateBoundsStatus } = useAssetDateBounds(needsWindow, requiresDateBounds, resourceType, resourceId, instrument, selectedModelId)

  const modelsDates = useMemo(() => {
    if (!wantsCoverage || !availabilityModels) {
      return EMPTY_MODELS_DATES
    }
    if (needsWindow) {
      // Swap has no live/historical distinction — always use the model's own current date,
      // matching modules/model-selector/selectors. Portfolio uses its actual rollover flag.
      const live = resourceType === "swap" ? true : (portfolioLive ?? true)
      return getWindowedCoverageModelsDates(availabilityModels, { earliestUploadedDate: earliestDate, latestUploadedDate: latestDate, live, requireBounds: requiresDateBounds })
    }
    return getCoverageModelsDates(availabilityModels)
  }, [wantsCoverage, needsWindow, availabilityModels, earliestDate, latestDate, resourceType, portfolioLive, requiresDateBounds])

  const [coverageMap, coverageStatus] = useCoverageMap(
    wantsCoverage ? resourceType : null,
    wantsCoverage ? resourceId : null,
    modelsDates,
  )

  return useMemo(() => {
    if (!wantsCoverage) {
      return { models, status: modelsStatus, requiresResourceCoverage: false }
    }
    if (modelsStatus === LOADING || availabilityStatus === LOADING || dateBoundsStatus === LOADING || coverageStatus === LOADING) {
      return { models: null, status: LOADING, requiresResourceCoverage: true }
    }
    if (modelsStatus !== OK || !models) {
      return { models: null, status: modelsStatus, requiresResourceCoverage: true }
    }
    if (availabilityStatus !== OK || dateBoundsStatus !== OK || coverageStatus !== OK) {
      return { models, status: OK, requiresResourceCoverage: false }
    }
    return {
      models: mergeModelsWithCoverage(models, coverageMap, selectedModelId, EMPTY_NO_RESTRICTION_MODELS),
      status: OK,
      requiresResourceCoverage: true,
    }
  }, [wantsCoverage, models, modelsStatus, availabilityStatus, dateBoundsStatus, coverageStatus, coverageMap, selectedModelId])
}
